// /admin/crm/templates — CRUD de templates Meta WA desde el panel.
// Lista las templates existentes (estado APPROVED/PENDING/REJECTED) y permite crear nuevas.
// El submit las sube a Meta vía edge function wa-create-template (acepta { custom: {...} }).

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const NAVY = '#1A3461'
const TEAL = '#00A8B5'
const BONE = '#F8F6F1'
const LINE = '#E2E8F0'

// Sugerencias rápidas — las "trampas" para reabrir ventana de 24hs.
const SUGERENCIAS = [
  {
    label: 'Consulta general (trampa apertura)',
    nombre: 'limpack_consulta',
    category: 'UTILITY',
    body: 'Hola {{1}}, soy {{2}} del equipo de Limpack. Queremos hacerte una consulta breve. ¿Tenés un minuto? Si podés, contestanos por acá y seguimos charlando.',
    footer: 'Limpack — Limpieza, resuelta cada mes.',
    ejemplos: ['María', 'Nico'],
  },
  {
    label: 'Seguimiento post-entrega',
    nombre: 'limpack_seguimiento_entrega',
    category: 'UTILITY',
    body: 'Hola {{1}}, soy {{2}} de Limpack. Queríamos saber cómo te fue con el kit que recibiste el {{3}}. ¿Llegó todo bien? Cualquier comentario o sugerencia nos ayuda mucho.',
    footer: 'Limpack — Limpieza, resuelta cada mes.',
    ejemplos: ['Marta', 'Nico', 'lunes'],
  },
  {
    label: 'Recordatorio cobro próximo',
    nombre: 'limpack_proximo_cobro',
    category: 'UTILITY',
    body: 'Hola {{1}}, te avisamos que el {{2}} hacemos el cobro mensual de tu plan {{3}} por ${{4}}. Si necesitás cambiar la tarjeta o pausar el mes, contestá este mensaje y lo coordinamos.',
    footer: 'Limpack — Limpieza, resuelta cada mes.',
    ejemplos: ['Carla', 'martes 1° de julio', 'Kit Familiar', '1.490'],
  },
  {
    label: 'Lead frío — invitación suave',
    nombre: 'limpack_invitacion',
    category: 'MARKETING',
    body: 'Hola {{1}}, soy {{2}} de Limpack. Te escribimos porque vimos que tenés interés en nuestro servicio de suscripción mensual de productos de limpieza. ¿Te interesa que te cuente cómo funciona?',
    footer: 'Limpack — Limpieza, resuelta cada mes.',
    ejemplos: ['Lucía', 'Nico'],
  },
]

export default function CRMTemplates() {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState('todos') // todos | APPROVED | PENDING | REJECTED
  const [editor, setEditor] = useState(null) // null | { name, category, body, footer, button_url, button_text, ejemplos[] }
  const [submitting, setSubmitting] = useState(false)
  const [feedback, setFeedback] = useState(null) // { ok, msg }

  async function cargar() {
    setLoading(true)
    try {
      const { data, error } = await supabase.functions.invoke('wa-list-templates', { body: {} })
      if (error) throw error
      setTemplates(data?.templates || [])
    } catch (e) {
      setFeedback({ ok: false, msg: 'No se pudo cargar templates: ' + (e.message || e) })
    }
    setLoading(false)
  }
  useEffect(() => { cargar() }, [])

  async function crearTemplate() {
    if (!editor) return
    setSubmitting(true); setFeedback(null)
    try {
      const components = []
      // BODY (siempre)
      const variables = [...editor.body.matchAll(/\{\{(\d+)\}\}/g)].map(m => parseInt(m[1], 10))
      const maxVar = variables.length ? Math.max(...variables) : 0
      const ejemplos = (editor.ejemplos || []).slice(0, maxVar)
      while (ejemplos.length < maxVar) ejemplos.push('ejemplo' + (ejemplos.length + 1))
      const bodyComp = { type: 'BODY', text: editor.body }
      if (maxVar > 0) bodyComp.example = { body_text: [ejemplos] }
      components.push(bodyComp)
      // FOOTER (opcional)
      if (editor.footer?.trim()) components.push({ type: 'FOOTER', text: editor.footer.trim() })
      // BUTTON URL (opcional)
      if (editor.button_url?.trim() && editor.button_text?.trim()) {
        components.push({
          type: 'BUTTONS',
          buttons: [{ type: 'URL', text: editor.button_text.trim(), url: editor.button_url.trim() }]
        })
      }
      const payload = {
        custom: {
          name: editor.name.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'),
          language: 'es_UY',
          category: editor.category || 'UTILITY',
          components,
        }
      }
      const { data, error } = await supabase.functions.invoke('wa-create-template', { body: payload })
      if (error) throw error
      const r = data?.results?.[0]
      if (r?.ok) {
        setFeedback({ ok: true, msg: `Template "${r.name}" enviada a Meta. Espera 24-48hs para aprobación.` })
        setEditor(null)
        setTimeout(cargar, 1500)
      } else {
        const errMsg = r?.response?.error?.error_user_msg || r?.response?.error?.message || JSON.stringify(r?.response)
        setFeedback({ ok: false, msg: `Meta rechazó: ${errMsg}` })
      }
    } catch (e) {
      setFeedback({ ok: false, msg: 'Error: ' + (e.message || e) })
    }
    setSubmitting(false)
  }

  const filtradas = templates.filter(t => filtro === 'todos' || t.status === filtro)
  const stats = useMemo(() => ({
    total: templates.length,
    APPROVED: templates.filter(t => t.status === 'APPROVED').length,
    PENDING: templates.filter(t => t.status === 'PENDING').length,
    REJECTED: templates.filter(t => t.status === 'REJECTED').length,
  }), [templates])

  return (
    <AdminLayout title="Templates WhatsApp">
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
        <Header stats={stats} onNuevo={() => setEditor(SUGERENCIAS[0])} onRefresh={cargar} loading={loading} />

        {feedback && (
          <div style={{
            margin: '12px 0', padding: '10px 14px', borderRadius: 8, fontSize: 13,
            background: feedback.ok ? 'rgba(22,163,74,.08)' : 'rgba(220,38,38,.08)',
            color: feedback.ok ? '#15803d' : '#b91c1c',
            border: `0.5px solid ${feedback.ok ? '#86efac' : '#fca5a5'}`,
          }}>
            {feedback.msg}
          </div>
        )}

        <div style={{ display: 'flex', gap: 6, margin: '12px 0' }}>
          {['todos', 'APPROVED', 'PENDING', 'REJECTED'].map(f => (
            <button key={f} onClick={() => setFiltro(f)} style={chipStyle(filtro === f)}>
              {f === 'todos' ? 'Todos' : statusLabel(f)} ({f === 'todos' ? stats.total : stats[f] || 0})
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando…</div>
        ) : filtradas.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', borderRadius: 8, border: `0.5px solid ${LINE}` }}>
            Sin templates para mostrar. Apretá <strong>+ Nueva template</strong>.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {filtradas.map(t => <TemplateRow key={t.id} t={t} onClonar={() => setEditor({
              name: t.name + '_v2', category: t.category, body: t.body, footer: '', button_text: '', button_url: '', ejemplos: [],
            })} />)}
          </div>
        )}
      </div>

      {editor && (
        <Editor
          editor={editor} setEditor={setEditor}
          onCancelar={() => { setEditor(null); setFeedback(null) }}
          onCrear={crearTemplate} submitting={submitting}
          sugerencias={SUGERENCIAS}
        />
      )}
    </AdminLayout>
  )
}

function Header({ stats, onNuevo, onRefresh, loading }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', margin: '8px 0 4px', flexWrap: 'wrap', gap: 10 }}>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: NAVY, letterSpacing: -0.4 }}>Templates WhatsApp</div>
        <div style={{ fontSize: 13, color: '#475569', marginTop: 3 }}>
          {stats.APPROVED} aprobadas · {stats.PENDING} pendientes · {stats.REJECTED} rechazadas
          {' · '}
          <Link to="/admin/crm" style={{ color: TEAL, textDecoration: 'none' }}>Volver al CRM →</Link>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onRefresh} disabled={loading} style={btnOutline}>
          {loading ? 'Cargando…' : 'Sincronizar con Meta'}
        </button>
        <button onClick={onNuevo} style={btnPrimary}>+ Nueva template</button>
      </div>
    </div>
  )
}

function TemplateRow({ t, onClonar }) {
  return (
    <div style={{ background: '#fff', border: `0.5px solid ${LINE}`, borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <code style={{ fontSize: 14, color: NAVY, fontWeight: 600, background: BONE, padding: '2px 8px', borderRadius: 4 }}>{t.name}</code>
            <span style={statusBadge(t.status)}>{statusLabel(t.status)}</span>
            <span style={{ fontSize: 11, color: '#64748b' }}>{t.category} · {t.language}</span>
          </div>
          <div style={{ marginTop: 6, fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap', maxHeight: 90, overflow: 'hidden' }}>
            {t.body}
          </div>
          {t.variables_count > 0 && (
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{t.variables_count} variable(s)</div>
          )}
        </div>
        <button onClick={onClonar} style={{ ...btnOutline, padding: '6px 12px', fontSize: 12, flexShrink: 0 }}>Clonar</button>
      </div>
    </div>
  )
}

function Editor({ editor, setEditor, onCancelar, onCrear, submitting, sugerencias }) {
  const variables = [...(editor.body || '').matchAll(/\{\{(\d+)\}\}/g)].map(m => parseInt(m[1], 10))
  const maxVar = variables.length ? Math.max(...variables) : 0
  const ejemplos = editor.ejemplos || []

  function setEj(i, v) {
    const nuevos = [...ejemplos]
    nuevos[i] = v
    setEditor({ ...editor, ejemplos: nuevos })
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, maxWidth: 920, width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 18px', borderBottom: `0.5px solid ${LINE}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: NAVY }}>Nueva template para Meta</div>
          <button onClick={onCancelar} style={{ background: 'transparent', border: 0, fontSize: 20, color: '#64748b', cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 18, padding: 18, overflow: 'auto', flex: 1 }}>
          {/* Form */}
          <div>
            <Label>Sugerencias rápidas (cargá una y editala)</Label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {sugerencias.map(s => (
                <button key={s.nombre} onClick={() => setEditor({
                  name: s.nombre, category: s.category, body: s.body, footer: s.footer || '',
                  button_text: '', button_url: '', ejemplos: s.ejemplos || [],
                })} style={chipSugerencia}>{s.label}</button>
              ))}
            </div>

            <Label>Nombre interno *</Label>
            <Input value={editor.name} onChange={e => setEditor({ ...editor, name: e.target.value })} placeholder="limpack_consulta" />
            <Hint>Solo minúsculas, números, guiones bajos. Meta lo usa para identificarla.</Hint>

            <Label>Categoría *</Label>
            <select value={editor.category} onChange={e => setEditor({ ...editor, category: e.target.value })} style={inputStyle}>
              <option value="UTILITY">UTILITY — consulta, seguimiento, transaccional</option>
              <option value="MARKETING">MARKETING — promoción, lead frío, ofertas</option>
              <option value="AUTHENTICATION">AUTHENTICATION — códigos OTP</option>
            </select>
            <Hint>UTILITY es más fácil de aprobar y no consume crédito Meta.</Hint>

            <Label>Cuerpo del mensaje *</Label>
            <textarea
              value={editor.body}
              onChange={e => setEditor({ ...editor, body: e.target.value })}
              rows={6}
              placeholder="Hola {{1}}, soy {{2}} de Limpack..."
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
            <Hint>Variables: usá <code>&#123;&#123;1&#125;&#125;</code>, <code>&#123;&#123;2&#125;&#125;</code>, etc.</Hint>

            {maxVar > 0 && (
              <>
                <Label>Ejemplos para cada variable (Meta los exige)</Label>
                {Array.from({ length: maxVar }).map((_, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                    <code style={{ fontSize: 12, color: '#64748b', minWidth: 36 }}>{`{{${i + 1}}}`}</code>
                    <Input value={ejemplos[i] || ''} onChange={e => setEj(i, e.target.value)} placeholder={`Ejemplo ${i + 1}`} />
                  </div>
                ))}
              </>
            )}

            <Label>Footer (opcional)</Label>
            <Input value={editor.footer} onChange={e => setEditor({ ...editor, footer: e.target.value })} placeholder="Limpack — Limpieza, resuelta cada mes." />

            <Label>Botón URL (opcional)</Label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 6 }}>
              <Input value={editor.button_text} onChange={e => setEditor({ ...editor, button_text: e.target.value })} placeholder="Ver mi plan" />
              <Input value={editor.button_url} onChange={e => setEditor({ ...editor, button_url: e.target.value })} placeholder="https://limpack.uy/..." />
            </div>
            <Hint>Si dejás ambos vacíos, no se agrega botón.</Hint>
          </div>

          {/* Preview */}
          <div>
            <Label>Preview WhatsApp</Label>
            <Preview editor={editor} ejemplos={ejemplos} />
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 8, lineHeight: 1.5 }}>
              <strong>Importante:</strong> Una vez que la envíes, Meta tarda 24-48hs en aprobarla. Si la rechaza, el motivo aparece arriba con la lista. Solo aprobadas pueden enviarse a clientes sin ventana de 24hs abierta.
            </div>
          </div>
        </div>

        <div style={{ padding: '12px 18px', borderTop: `0.5px solid ${LINE}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onCancelar} style={btnOutline}>Cancelar</button>
          <button onClick={onCrear} disabled={!editor.name?.trim() || !editor.body?.trim() || submitting} style={btnPrimary}>
            {submitting ? 'Enviando a Meta…' : 'Enviar a Meta para aprobación'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Preview({ editor, ejemplos }) {
  let body = editor.body || ''
  ejemplos?.forEach((e, i) => { if (e) body = body.replaceAll(`{{${i + 1}}}`, e) })
  return (
    <div style={{ background: '#E5DDD5', padding: 14, borderRadius: 10, minHeight: 200, backgroundImage: 'radial-gradient(rgba(0,0,0,.04) 1px, transparent 1px)', backgroundSize: '14px 14px' }}>
      <div style={{ background: '#fff', borderRadius: 8, borderTopLeftRadius: 2, padding: '8px 11px 5px', maxWidth: '85%', boxShadow: '0 1px 2px rgba(0,0,0,.1)', fontSize: 13.5, lineHeight: 1.4, color: '#0F1F3D', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {body || <span style={{ color: '#94a3b8' }}>El mensaje va a aparecer acá…</span>}
        {editor.footer && (
          <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>{editor.footer}</div>
        )}
        {editor.button_text && editor.button_url && (
          <div style={{ marginTop: 8, paddingTop: 6, borderTop: '0.5px solid #e2e8f0', color: '#00A5F4', fontSize: 13, textAlign: 'center', fontWeight: 500 }}>
            🔗 {editor.button_text}
          </div>
        )}
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, textAlign: 'right' }}>14:32 ✓✓</div>
      </div>
    </div>
  )
}

// Helpers
function Label({ children }) { return <div style={{ fontSize: 12, color: '#475569', fontWeight: 600, margin: '12px 0 4px' }}>{children}</div> }
function Hint({ children }) { return <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>{children}</div> }
function Input(props) { return <input {...props} style={{ ...inputStyle, ...(props.style || {}) }} /> }

const inputStyle = { width: '100%', padding: '8px 11px', border: `0.5px solid ${LINE}`, borderRadius: 6, fontSize: 13, color: '#0f1f3d', outline: 'none', fontFamily: 'inherit', background: '#fff' }
const btnPrimary = { padding: '8px 16px', background: NAVY, color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer' }
const btnOutline = { padding: '8px 16px', background: '#fff', color: NAVY, border: `0.5px solid ${LINE}`, borderRadius: 6, fontWeight: 500, fontSize: 13, cursor: 'pointer' }
const chipSugerencia = { padding: '5px 10px', background: BONE, color: NAVY, border: `0.5px solid ${LINE}`, borderRadius: 999, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }

function chipStyle(on) {
  return {
    padding: '5px 12px',
    background: on ? NAVY : '#fff',
    color: on ? '#fff' : NAVY,
    border: `0.5px solid ${on ? NAVY : LINE}`,
    borderRadius: 999, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
  }
}

function statusLabel(s) {
  return { APPROVED: 'Aprobada', PENDING: 'Pendiente', REJECTED: 'Rechazada', PAUSED: 'Pausada', DISABLED: 'Deshabilitada' }[s] || s
}
function statusBadge(s) {
  const colors = {
    APPROVED: { bg: 'rgba(22,163,74,.1)', fg: '#15803d' },
    PENDING: { bg: 'rgba(217,119,6,.1)', fg: '#a16207' },
    REJECTED: { bg: 'rgba(220,38,38,.1)', fg: '#b91c1c' },
    PAUSED: { bg: 'rgba(100,116,139,.1)', fg: '#475569' },
  }[s] || { bg: 'rgba(100,116,139,.1)', fg: '#475569' }
  return { fontSize: 10.5, padding: '2px 8px', borderRadius: 4, fontWeight: 600, background: colors.bg, color: colors.fg }
}
